import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError, tap, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthResponse } from '../models/auth-response.model';
import { environment } from '../../../environments/environment';
import { RegisterRequest } from '../models/register-request.model';
import { UpdateUserResponse } from '../models/upadte-user-response';
import { UpdateUserRequest } from '../models/update-user-request.model';
import { UserDto } from '../models/user.model';
import { ResetPasswordRequest } from '../models/reset-password-request.model';

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	private readonly AUTH_URL = '/api/Auth';
	private currentUserSubject: BehaviorSubject<AuthResponse | null>;
	public currentUser: Observable<AuthResponse | null>;

	baseUrl!: string;

	constructor(
		private http: HttpClient,
		private router: Router
	) {
		const storedUser = localStorage.getItem('currentUser');
		this.currentUserSubject = new BehaviorSubject<AuthResponse | null>(
			storedUser ? JSON.parse(storedUser) : null
		);
		this.currentUser = this.currentUserSubject.asObservable();
		this.baseUrl = environment.apiUrl
	}

	public get currentUserValue(): AuthResponse | null {
		return this.currentUserSubject.value;
	}

	login(username: string, password: string): Observable<AuthResponse> {
		return this.http.post<AuthResponse>(`${this.baseUrl + this.AUTH_URL}/login`, { username, password }, { withCredentials: true })
			.pipe(
				tap(response => {
					localStorage.setItem('currentUser', JSON.stringify(response));
					localStorage.setItem('accessToken', response.accessToken);
					localStorage.setItem('refreshToken', response.refreshToken);
					this.currentUserSubject.next(response);
				})
			);
	}

		logout(): void {
				const accessToken = localStorage.getItem('accessToken');
				
				const headers = new HttpHeaders({
						'Authorization': `Bearer ${accessToken}`
				});

				this.http.post(`${this.baseUrl + this.AUTH_URL}/logout`, {}, { headers: headers, withCredentials: true })
						.pipe(finalize(() => {this.clearLocalData()}))
						.subscribe();
		}

		private clearLocalData(): void {
				localStorage.removeItem('currentUser');
				localStorage.removeItem('accessToken');
				localStorage.removeItem('refreshToken');
				this.currentUserSubject.next(null);
				this.router.navigate(['/login']);
		}

	refreshToken(): Observable<AuthResponse> {
		const accessToken = localStorage.getItem('accessToken');
		const refreshToken = localStorage.getItem('refreshToken');
		
		return this.http.post<AuthResponse>(`${this.AUTH_URL}/refresh-token`, {
			accessToken,
			refreshToken
		}, { withCredentials: true }).pipe(
			tap(response => {
				localStorage.setItem('currentUser', JSON.stringify(response));
				localStorage.setItem('accessToken', response.accessToken);
				localStorage.setItem('refreshToken', response.refreshToken);
				this.currentUserSubject.next(response);
			})
		);
	}

	updateUserRole(userId: number, roleId: number): Observable<void> {
		const token = localStorage.getItem('accessToken');
		const headers = new HttpHeaders({
			'Authorization': `Bearer ${token}`
		});

		return this.http.put<void>(
			`${this.baseUrl + this.AUTH_URL}/update-role/${userId}`, 
			{ roleId }, 
			{ headers: headers, withCredentials: true }
		);
	}

	hasRole(role: string): boolean {
		return this.currentUserValue?.role === role;
	}

	isAdmin(): boolean {
		return this.hasRole('Admin');
	}

	isEmployee(): boolean {
		return this.hasRole('Employee');
	}

	canApproveAppointments(): boolean {
		return this.isAdmin();
	}

		registerUser(registerRequestDto: RegisterRequest): Observable<void> {
		const token = localStorage.getItem('accessToken');
			const headers = new HttpHeaders({
				'Authorization': `Bearer ${token}`
 		});		
		
		return this.http.post<void>(`${this.baseUrl + this.AUTH_URL}/register`, registerRequestDto, 
				{ headers: headers, withCredentials: true }
			);
	}

	getAllUsers(): Observable<UserDto[]> {
				return this.http.get<UserDto[]>(`${this.baseUrl}${this.AUTH_URL}/users`);
		}

		getUserById(userId: number): Observable<UserDto> {
				return this.http.get<UserDto>(`${this.baseUrl}${this.AUTH_URL}/users/${userId}`);
		}

		updateUser(request: UpdateUserRequest): Observable<UpdateUserResponse> {
		const token = localStorage.getItem('accessToken');
			const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`
 		});

				return this.http.put<UpdateUserResponse>(
					`${this.baseUrl}${this.AUTH_URL}/users/${request.userId}`,
					request, { headers: headers, withCredentials: true }
				);
		}

		resetPassword(request: ResetPasswordRequest): Observable<any> {
		const token = localStorage.getItem('accessToken');
			const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`
 		});

				return this.http.post(`${this.baseUrl}${this.AUTH_URL}/reset-password`, request,
							{ headers: headers, withCredentials: true });
		}

	deleteUser(userId: number): Observable<{ message: string }> {
		const token = localStorage.getItem('accessToken');
			const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`
 		});

 		return this.http.delete<{ message: string }>(`${this.baseUrl}${this.AUTH_URL}/users/${userId}`, { headers: headers, withCredentials: true });
	}

	getCurrentUserId(): number {
			return this.currentUserValue?.userId ?? null;
	}

	getUserRole(): string {
		return this.currentUserValue?.role;
	}
}